import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Settings, Bell, BellOff, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { playNotificationSound, resumeAudioContext } from '@/utils/notificationSound';

interface NotificationPreferences {
  id?: string;
  user_id: string;
  triage_completed: boolean;
  patient_assigned: boolean;
  patient_unassigned: boolean;
  red_flag_alert: boolean;
  notifications_enabled: boolean;
  sound_enabled: boolean;
}

const defaultPreferences: Omit<NotificationPreferences, 'user_id'> = {
  triage_completed: true,
  patient_assigned: true,
  patient_unassigned: true,
  red_flag_alert: true,
  notifications_enabled: true,
  sound_enabled: true,
};

export default function SupervisorNotificationPreferences() {
  const { user } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);

  const fetchPreferences = async () => {
    if (!user?.id) return;

    const { data, error } = await supabase
      .from('supervisor_notification_preferences')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching preferences:', error);
    } else if (data) {
      setPreferences(data as NotificationPreferences);
    } else {
      // No preferences exist yet, use defaults
      setPreferences({
        user_id: user.id,
        ...defaultPreferences,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      fetchPreferences();
    }
  }, [open, user?.id]);

  const handleToggle = (key: keyof Omit<NotificationPreferences, 'id' | 'user_id'>) => {
    if (!preferences) return;
    setPreferences({
      ...preferences,
      [key]: !preferences[key],
    });
  };

  const handleSave = async () => {
    if (!preferences || !user?.id) return;

    setSaving(true);

    const { id, ...prefsData } = preferences;

    if (id) {
      // Update existing
      const { error } = await supabase
        .from('supervisor_notification_preferences')
        .update({
          triage_completed: prefsData.triage_completed,
          patient_assigned: prefsData.patient_assigned,
          patient_unassigned: prefsData.patient_unassigned,
          red_flag_alert: prefsData.red_flag_alert,
          notifications_enabled: prefsData.notifications_enabled,
          sound_enabled: prefsData.sound_enabled,
        })
        .eq('id', id);

      if (error) {
        console.error('Error updating preferences:', error);
        toast.error('Failed to save preferences');
      } else {
        toast.success('Notification preferences saved');
        setOpen(false);
      }
    } else {
      // Insert new
      const { data, error } = await supabase
        .from('supervisor_notification_preferences')
        .insert({
          user_id: user.id,
          triage_completed: prefsData.triage_completed,
          patient_assigned: prefsData.patient_assigned,
          patient_unassigned: prefsData.patient_unassigned,
          red_flag_alert: prefsData.red_flag_alert,
          notifications_enabled: prefsData.notifications_enabled,
          sound_enabled: prefsData.sound_enabled,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating preferences:', error);
        toast.error('Failed to save preferences');
      } else {
        setPreferences(data as NotificationPreferences);
        toast.success('Notification preferences saved');
        setOpen(false);
      }
    }

    setSaving(false);
  };

  const notificationTypes = [
    {
      key: 'triage_completed' as const,
      label: 'Assessment Completed',
      description: 'When an assigned patient completes an assessment',
    },
    {
      key: 'patient_assigned' as const,
      label: 'Patient Assigned',
      description: 'When a new patient is assigned to you',
    },
    {
      key: 'patient_unassigned' as const,
      label: 'Patient Unassigned',
      description: 'When a patient is removed from your assignments',
    },
    {
      key: 'red_flag_alert' as const,
      label: 'Red Flag Alerts',
      description: 'When an assigned patient triggers red flag symptoms',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Preferences
          </DialogTitle>
          <DialogDescription>
            Choose which notifications you want to receive
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-muted-foreground">
            Loading preferences...
          </div>
        ) : preferences ? (
          <div className="space-y-6 py-4">
            {/* Master Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                {preferences.notifications_enabled ? (
                  <Bell className="h-5 w-5 text-primary" />
                ) : (
                  <BellOff className="h-5 w-5 text-muted-foreground" />
                )}
                <div>
                  <Label htmlFor="notifications-enabled" className="text-base font-medium">
                    Enable Notifications
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Master toggle for all notifications
                  </p>
                </div>
              </div>
              <Switch
                id="notifications-enabled"
                checked={preferences.notifications_enabled}
                onCheckedChange={() => handleToggle('notifications_enabled')}
              />
            </div>

            <Separator />

            {/* Individual Toggles */}
            <div className="space-y-4">
              <p className="text-sm font-medium text-muted-foreground">
                Notification Types
              </p>
              {notificationTypes.map((type) => (
                <div
                  key={type.key}
                  className={`flex items-center justify-between py-3 ${
                    !preferences.notifications_enabled ? 'opacity-50' : ''
                  }`}
                >
                  <div>
                    <Label htmlFor={type.key} className="font-medium">
                      {type.label}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {type.description}
                    </p>
                  </div>
                  <Switch
                    id={type.key}
                    checked={preferences[type.key]}
                    onCheckedChange={() => handleToggle(type.key)}
                    disabled={!preferences.notifications_enabled}
                  />
                </div>
              ))}
            </div>

            <Separator />

            {/* Sound Settings */}
            <div className={`flex items-center justify-between p-4 rounded-lg bg-muted/50 ${
              !preferences.notifications_enabled ? 'opacity-50' : ''
            }`}>
              <div className="flex items-center gap-3">
                {preferences.sound_enabled ? (
                  <Volume2 className="h-5 w-5 text-primary" />
                ) : (
                  <VolumeX className="h-5 w-5 text-muted-foreground" />
                )}
                <div>
                  <Label htmlFor="sound-enabled" className="text-base font-medium">
                    Sound Alerts
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Play sounds for urgent notifications
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={async () => {
                    await resumeAudioContext();
                    playNotificationSound();
                  }}
                  disabled={!preferences.notifications_enabled || !preferences.sound_enabled}
                  className="text-xs"
                >
                  Test
                </Button>
                <Switch
                  id="sound-enabled"
                  checked={preferences.sound_enabled}
                  onCheckedChange={() => handleToggle('sound_enabled')}
                  disabled={!preferences.notifications_enabled}
                />
              </div>
            </div>

            <Separator />

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
